import "./SupportLayout.css";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  Dropdown,
  Modal,
  Accordion,
  Offcanvas,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { DateRange, DateRangePicker } from "react-date-range";
import { addDays } from "date-fns";
import Instance from "../../Utils/Axios";
import { showToast } from "../../Utils/index";
import { Link } from "react-router-dom";
import "react-date-range/dist/styles.css";
import PremiumLoader from "../Loader/PremiumLoader";
import CustomPagination from "../CustomPagination/CustomPagination";
import useKeyScroll from "../../Hooks/useKeyScroll";
const DOC_URL = "https://pr.webandapi.com";

const formatTicketDate = (dateTime) => {
  if (!dateTime) return "-";
  const normalized = dateTime.replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return dateTime;
  }

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${dateFormatter.format(date)} • ${timeFormatter.format(date)}`;
};

const TruncatedTooltip = ({ text, id }) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsTruncated(element.scrollHeight > element.clientHeight);
    }
  }, [text]);

  const content = (
    <div ref={textRef} className="line_clamp_2">
      {text}
    </div>
  );

  if (isTruncated) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={id}>{text}</Tooltip>}
      >
        {content}
      </OverlayTrigger>
    );
  }

  return content;
};

export default function SupportLayout() {
  const scrollContainerRef = useKeyScroll();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(0);
  const [packagesApplied, setPackagesApplied] = useState(0);
  const [readApplied, setReadApplied] = useState(0);
  const [show, setShow] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    createdOn: "",
  });
  const [showResolve, setshowResolve] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketDetail, setTicketDetail] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ status: "", createdOn: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [chatContent, setChatContent] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const chatContainerRef = useRef(null);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const today = new Date();
  const [selectionRange, setSelectionRange] = useState({
    startDate: today,
    endDate: today,
    key: "selection",
  });
  const [activeQuickRange, setActiveQuickRange] = useState("");
  const [isDateFilterApplied, setIsDateFilterApplied] = useState(false);

  const handleSelect = (ranges) => {
    setSelectionRange({
      ...ranges.selection,
      key: "selection"
    });
    setActiveQuickRange("");
    setIsDateFilterApplied(true);
  };

  const handleQuickSelect = (type) => {
    const today = new Date();
    let start = today;
    let end = today;

    switch (type) {
      case "Today":
        break;
      case "Yesterday":
        start = addDays(today, -1);
        end = addDays(today, -1);
        break;
      case "This Week":
        start = addDays(today, -today.getDay());
        break;
      case "Last Week":
        start = addDays(today, -today.getDay() - 7);
        end = addDays(today, -today.getDay() - 1);
        break;
      case "This Month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case "Last Month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        break;
    }

    setSelectionRange({
      startDate: start,
      endDate: end,
      key: "selection",
    });
    setActiveQuickRange(type);
    setIsDateFilterApplied(true);
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  // const handleDateChange = (date) => {
  //   setSelectedDate(date);
  // };

  const fetchDetails = (ticket) => {
    Instance.post("/user/support/details", {
      supportId: ticket?.supportId,
    })
      .then((response) => {
        setTicketDetail(response.data.message);
        setChatContent(response.data.message.chat || []);
      })
      .catch((error) => {
        console.error("error fetch ticket detail", error);
      });
  };

  useEffect(() => {
    const fetchTicketsData = async () => {
      try {
        setListLoading(true);
        const payload = {
          pageNumber: currentPage,
          pageSize: rowsPerPage,
        };

        if (sortConfig.key) {
          payload.orderBy = sortConfig.key;
          payload.orderByType =
            sortConfig.direction === "ascending" ? "ASC" : "DESC";
        }

        if (appliedFilters.createdOn) {
          payload.createdOn = appliedFilters.createdOn;
        }

        if (appliedFilters.status) {
          payload.status = appliedFilters.status;
        }

        const response = await Instance.post("user/support/getList", payload);
        if (response.data.resCode === "200") {
          setTickets(response.data.message?.tickets || []);
          setTotalPages(response.data.totalPages || 1);
          setTotalRecords(response.data.totalRecords || response.data.totalRecord || 0);
        } else {
          console.error("Failed to fetch tickets:", response.data.resMessage);
        }
      } catch (error) {
        console.error("Error fetching tickets:", error);
      } finally {
        setListLoading(false);
        setLoading(false);
      }
    };

    fetchTicketsData();
  }, [currentPage, rowsPerPage, sortConfig, appliedFilters]);

  useEffect(() => {
    let intervalId;
    if (selectedTicket) {
      intervalId = setInterval(() => {
        Instance.post("/user/support/details", {
          supportId: selectedTicket.supportId,
        })
          .then((response) => {
            if (response.data?.message) {
              const newChat = response.data.message.chat || [];
              setChatContent((prevChat) => {
                if (prevChat.length !== newChat.length || JSON.stringify(prevChat) !== JSON.stringify(newChat)) {
                  return newChat;
                }
                return prevChat;
              });
              setTicketDetail((prevDetail) => {
                if (JSON.stringify(prevDetail) !== JSON.stringify(response.data.message)) {
                  return response.data.message;
                }
                return prevDetail;
              });
            }
          })
          .catch((error) => {
            console.error("Error polling ticket detail", error);
          });
      }, 5000);
    }
    return () => clearInterval(intervalId);
  }, [selectedTicket]);

  const handleResolve = async (e) => {
    e.preventDefault();
    try {
      // const token = localStorage.getItem("tokenId");
      const response = await Instance.post(`/user/support/update`, {
        // tokenId: token,
        supportId: selectedTicket.supportId,
        status: "Resolved",
      });
      if (response.data.resCode === "200") {
        showToast("Ticket Resolved!", "success", "success");
        setshowResolve(false);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        // console.log(response.data.resMessage);
      }
    } catch (error) {
      console.error("Error updating the ticket status:", error);
    }
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() === "" && !selectedFile) return;

    if (!selectedTicket || !selectedTicket.supportId) {
      console.error("No ticket ID selected");
      showToast("Please Select a Ticket !", "error", "error");
      return;
    }

    try {
      setIsUploadingAttachment(true);
      let documentUrl = "";

      if (selectedFile) {
        // const token = localStorage.getItem("tokenId");
        const formDataToSubmit = new FormData();
        // formDataToSubmit.append("tokenId", token);
        formDataToSubmit.append("fileUpload", selectedFile);

        const uploadResponse = await Instance.post(
          "site/upload/documents",
          formDataToSubmit,
          {
            headers: {
              "content-type": "multipart/form-data",
            },
          }
        );

        if (uploadResponse?.data?.resCode === "200") {
          documentUrl = uploadResponse.data.documentUrl;
        } else {
          console.error("Upload failed:", uploadResponse?.data);
          showToast("Failed to upload file. Please try again.", "error", "error");
          setIsUploadingAttachment(false);
          return;
        }
      }

      const response = await Instance.post("/user/support/message", {
        supportId: selectedTicket.supportId,
        comment: inputValue.trim(),
        docPath: documentUrl,
      });

      if (response.data.resCode === "200") {
        setChatContent((prevMessages) => [
          ...prevMessages,
          {
            chatId: response.data.id || Date.now(),
            userType: "User",
            comment: inputValue,
            docPath: documentUrl,
          },
        ]);
        setInputValue("");
        setSelectedFile(null);
        setSelectedFileType(null);
      } else {
        console.error(response.data.resMessage);
      }
    } catch (error) {
      console.error("Error sending the message:", error);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleAttachmentIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageIconClick = () => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const handleFileSelect = (event, type) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "application/pdf",
      ];
      const isVideo = file.type.startsWith("video/");
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type) && !isVideo) {
        showToast("Invalid file type. Please upload JPG, PNG, WEBP, PDF, or Video.", "error", "error");
        event.target.value = null;
        return;
      }

      if (file.size > maxSize) {
        showToast("File size exceeds 5MB limit.", "error", "error");
        event.target.value = null;
        return;
      }

      setSelectedFile(file);
      setSelectedFileType(type);
    }
    event.target.value = null;
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handlePackageChange = (newPackage, isChecked) => {
    setSelectedPackages(isChecked ? [newPackage] : []);
  };

  const calculateFiltersApplied = () => {
    let count = 0;
    if (isDateFilterApplied) count++;
    count += selectedPackages.length;
    setFiltersApplied(count);
  };
  const calculatePackagesApplied = () => {
    setPackagesApplied(selectedPackages.length);
  };
  const handleApplyFilters = async () => {
    calculateFiltersApplied();
    calculatePackagesApplied();
    handleClose();
    setCurrentPage(1);

    const newAppliedFilters = { status: "", createdOn: null };

    if (isDateFilterApplied && selectionRange.startDate && selectionRange.endDate) {
      newAppliedFilters.createdOn = {
        startDate: selectionRange.startDate.toISOString().split("T")[0],
        endDate: selectionRange.endDate.toISOString().split("T")[0],
      };
    }

    if (selectedPackages.length && selectedPackages[0] !== "") {
      newAppliedFilters.status = selectedPackages[0];
    }

    setAppliedFilters(newAppliedFilters);
  };

  const handleClearFilters = async () => {
    setFiltersApplied(0);
    const today = new Date();
    setSelectionRange({
      startDate: today,
      endDate: today,
      key: "selection",
    });
    setActiveQuickRange("");
    setIsDateFilterApplied(false);
    setSelectedPackages([]);
    setPackagesApplied(0);
    setCurrentPage(1);
    setAppliedFilters({ status: "", createdOn: null });
  };

  const handleTicketSelect = (ticket) => {
    fetchDetails(ticket);
    setSelectedTicket(ticket);
  };

  useEffect(() => {
    if (tickets?.length > 0) {
      fetchDetails(tickets[0]);
      setSelectedTicket(tickets[0]);
    }
  }, [tickets]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chatContent]);

  const handleShowResolve = () => {
    setshowResolve(true);
  };
  const handleNotShowResolve = () => {
    setshowResolve(false);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const currentTickets = Array.isArray(tickets) ? tickets : [];

  const renderSortIcon = (field) => {
    if (sortConfig.key !== field) {
      return (
        <FontAwesomeIcon
          icon={faArrowDown}
          style={{ marginLeft: 6, opacity: 0.3 }}
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === "ascending" ? faArrowUp : faArrowDown}
        style={{ marginLeft: 6, opacity: 1 }}
      />
    );
  };

  const handleSortChange = (selectedColumn) => {
    setSortConfig((prev) => ({
      key: selectedColumn.sortField,
      direction:
        prev.key === selectedColumn.sortField &&
          prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            TICKET NO {renderSortIcon("supportId")}
          </div>
        ),
        selector: (row) => row.supportId,
        sortable: true,
        sortField: "supportId",
        width: "180px",
        cell: (row) => (
          <div className="d-flex align-items-center gap-3">
            <input
              type="checkbox"
              className="support_checkbox"
              checked={selectedTicket?.supportId === row.supportId}
              onChange={() => handleTicketSelect(row)}
              onClick={(e) => e.stopPropagation()}
            />
            <span>{row.supportId}</span>
          </div>
        ),
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            QUERY SUB
          </div>
        ),
        selector: (row) => row.subject,
        width: "300px",
        cell: (row, index) => (
          <TruncatedTooltip
            text={row.subject}
            id={`tooltip-table-sub-${index}`}
          />
        ),
      },
      {
        name: (
          <div style={{ display: "flex", alignItems: "center" }}>
            TICKET RAISED ON {renderSortIcon("createdOn")}
          </div>
        ),
        selector: (row) => row.createdOn,
        sortable: true,
        sortField: "createdOn",
        width: "180px",
        cell: (row) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {formatTicketDate(row.createdOn).split(" • ")[0]}
          </div>
        ),
      },
      {
        name: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            STATUS {renderSortIcon("status")}
          </div>
        ),
        selector: (row) => row.status,
        sortable: true,
        sortField: "status",
        center: true,
        width: "160px",
        cell: (row) => (
          <span
            className={`status_badge ${row.status === "OPEN" ? "status_open" : "status_closed"
              }`}
          >
            {row.status ? row.status.toLowerCase() : "N/A"}
          </span>
        ),
      },
    ],
    [sortConfig, selectedTicket]
  );

  const customStyles = {
    table: {
      style: {
        minWidth: "max-content",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        minHeight: "52px",
        minWidth: "max-content",
      },
    },
    headCells: {
      style: {
        fontSize: "13px",
        textTransform: "uppercase",
        fontWeight: "700",
        color: "#444444",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    cells: {
      style: {
        color: "#6c757d",
        fontSize: "14px",
        fontWeight: "500",
        whiteSpace: "normal",
        flexShrink: 0,
        borderRight: "none",
      },
    },
    rows: {
      style: {
        minHeight: "70px",
        minWidth: "max-content",
      },
      highlightOnHoverStyle: {
        backgroundColor: "#f5f6ff",
        outline: "none",
        transition: "background-color 0.2s ease",
      },
    },
  };

  return (
    <>
      <div className="support_layout">
        <>
          <div className="ticket_list">
            <div className="ticket_list_btns">
              <Link to="/customer/support/add" target="_blank" className="text-decoration-none">
                <button className="add_new_query_btn d-flex align-items-center">
                  <img
                    className="me-2"
                    src="/build/img/Icons/Vector.png"
                    alt="add"
                    height={16}
                    width={16}
                    style={{ filter: 'brightness(0) invert(1)' }}
                  />
                  Add New Query
                </button>
              </Link>
              <div className="d-flex align-items-center gap-3">
                <button className="Support_filter_btn d-flex align-items-center" onClick={handleShow}>
                  Filters
                  <img
                    className="ms-2"
                    src="/build/img/Icons/down_vector.svg"
                    alt="filter"
                    width={16}
                    height={16}
                  />
                </button>
                <Offcanvas show={show} onHide={handleClose} placement="start" className="filter_offcanvas">
                  <Offcanvas.Header closeButton className="filter_offcanvas_header">
                    <Offcanvas.Title className="filter_offcanvas_title">
                      {filtersApplied} filters applied
                    </Offcanvas.Title>
                  </Offcanvas.Header>
                  <Offcanvas.Body className="filter_offcanavas_body">
                    <Accordion alwaysOpen flush>
                      <Accordion.Item eventKey="0" className="accordian_item">
                        <Accordion.Header className="filter_accordian">
                          Date Range
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="quick_ranges_container">
                            {["Today", "Yesterday", "This Week", "Last Week", "This Month", "Last Month"].map((label) => (
                              <button
                                key={label}
                                className={`quick_range_btn ${activeQuickRange === label ? "active" : ""}`}
                                onClick={() => handleQuickSelect(label)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                          <div className="date_picker_container">
                            <DateRange
                              ranges={[selectionRange]}
                              onChange={handleSelect}
                              months={1}
                              direction="horizontal"
                              maxDate={new Date()}
                              showMonthAndYearPickers={true}
                              moveRangeOnFirstSelection={false}
                              navigatorRenderer={(focusedDate, changeShownDate) => {
                                const months = [
                                  "January", "February", "March", "April", "May", "June",
                                  "July", "August", "September", "October", "November", "December"
                                ];
                                const currentYear = new Date().getFullYear();
                                const years = Array.from({ length: 50 }, (_, i) => currentYear - 25 + i);

                                return (
                                  <div className="rdrMonthAndYearPickers custom_nav_pickers">
                                    <div className="calendar_nav_wrapper">
                                      <Dropdown onSelect={(m) => changeShownDate(new Date(focusedDate.setMonth(parseInt(m))))}>
                                        <Dropdown.Toggle variant="light" className="calendar_nav_btn">
                                          {months[focusedDate.getMonth()]}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="calendar_nav_menu scrollable_menu">
                                          {months.map((m, i) => (
                                            <Dropdown.Item key={m} eventKey={i}>{m}</Dropdown.Item>
                                          ))}
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    </div>

                                    <div className="calendar_nav_wrapper">
                                      <Dropdown onSelect={(y) => changeShownDate(new Date(focusedDate.setFullYear(parseInt(y))))}>
                                        <Dropdown.Toggle variant="light" className="calendar_nav_btn">
                                          {focusedDate.getFullYear()}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className="calendar_nav_menu scrollable_menu">
                                          {years.map((y) => (
                                            <Dropdown.Item key={y} eventKey={y}>{y}</Dropdown.Item>
                                          ))}
                                        </Dropdown.Menu>
                                      </Dropdown>
                                    </div>
                                  </div>
                                );
                              }}
                            />
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="1" className="accordian_item">
                        <Accordion.Header className="filter_accordian ">
                          <span>Status</span>
                          <span className="filter_accordian_text">
                            {packagesApplied} selected{" "}
                          </span>
                        </Accordion.Header>
                        <Accordion.Body className="filter_package_body pb-3">
                          <Form
                            value={filters.status}
                            onChange={handleFilterChange}
                          >
                            {/* {["radio"].map((type) => ( */}
                            <div key={`inline-radio`} className="">
                              <Form.Check
                                label="All"
                                name="status"
                                type="radio"
                                id={`default-radio-1`}
                                checked={selectedPackages.includes("")}
                                onChange={(e) =>
                                  handlePackageChange("", e.target.checked)
                                }
                              />
                              <Form.Check
                                label="Open"
                                name="status"
                                type="radio"
                                id={`default-radio-2`}
                                checked={selectedPackages.includes("Open")}
                                onChange={(e) =>
                                  handlePackageChange("Open", e.target.checked)
                                }
                              />
                              <Form.Check
                                label="Closed"
                                name="status"
                                type="radio"
                                id={`default-radio-3`}
                                checked={selectedPackages.includes("Resolved")}
                                onChange={(e) =>
                                  handlePackageChange("Resolved", e.target.checked)
                                }
                              />
                            </div>
                            {/* ))} */}
                          </Form>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                    <div className="mt-4 mb-4 filter_btns">
                      <button
                        className="filter_clear_btn"
                        onClick={handleClearFilters}
                      >
                        Clear
                      </button>
                      <button
                        className="filter_apply_btn"
                        onClick={handleApplyFilters}
                      >
                        Apply
                      </button>
                    </div>
                  </Offcanvas.Body>
                </Offcanvas>
                <div className="d-flex align-items-center flex-wrap ms-2 gap-2">
                  {filtersApplied > 0 && (
                    <div className="d-flex align-items-center border rounded-pill px-3 py-1" style={{ fontSize: '13px', color: '#d9534f', borderColor: '#d9534f', backgroundColor: '#fdf2f2' }}>
                      <span className="me-2 fw-medium">
                        {filtersApplied} filters applied
                      </span>
                      <span
                        aria-hidden="true"
                        style={{ cursor: "pointer", fontSize: "18px", lineHeight: "1", marginLeft: "4px" }}
                        onClick={handleClearFilters}
                      >
                        &times;
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="ticket_table_container">
              <div
                className="ticket_table_scroll_wrapper"
                ref={scrollContainerRef}
                tabIndex="0"
                style={{ outline: "none" }}
              >
                {listLoading ? (
                  <div className="text-center py-5 d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                    <PremiumLoader
                      fullPage={false}
                      text={loading ? "Loading tickets..." : "Updating list..."}
                    />
                  </div>
                ) : currentTickets.length === 0 ? (
                  <div className="no_query_message support-empty-state">
                    NO QUERY AVAILABLE
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={currentTickets}
                    customStyles={customStyles}
                    sortServer
                    onSort={handleSortChange}
                    sortIcon={<></>}
                    highlightOnHover
                    pointerOnHover
                    dense={false}
                    onRowClicked={(row) => handleTicketSelect(row)}
                    conditionalRowStyles={[
                      {
                        when: (row) =>
                          selectedTicket?.supportId === row.supportId,
                        style: {
                          backgroundColor: "#f5f6ff",
                        },
                      },
                    ]}
                    className="support-table-react"
                  />
                )}
              </div>
              <div className="support-table-footer-pagination">
                <CustomPagination
                  totalItems={totalRecords}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </div>
            </div>
          </div>
          <div className="ticket_chatbox">
            <div className="chatbox_title mb-2">
              {ticketDetail.length !== 0 ? (
                <div>
                  <h4 style={{ marginBottom: "2px" }}>Ticket No: {ticketDetail?.supportId}</h4>
                  <span className="ticket_chatbox_date">{formatTicketDate(ticketDetail?.createdOn)}</span>
                </div>
              ) : (<h4>Ticket Details</h4>)}

              {ticketDetail?.status === "OPEN" ? (
                <p
                  onClick={handleShowResolve}
                  style={{
                    cursor: "pointer",
                    display: "inline-block",
                    background: "linear-gradient(90deg,#6a6ff5,#4c5bd4)",
                    color: "#fff",
                    padding: "6px 18px",
                    borderRadius: "40px",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginLeft: "auto"
                  }}
                >
                  Resolve
                </p>
              ) : (
                ""
              )}
              <Modal
                show={showResolve}
                onHide={handleNotShowResolve}
                centered
                className="support_resolve_modal"
              >
                <Modal.Body>
                  <div className="support_modal_img">
                    <img
                      className="support_done_img"
                      src="/build/Done.svg"
                      alt="Resolve Ticket"
                      width={80}
                      height={80}
                    />
                  </div>

                  <div className="support_modal_text">
                    <h5 className="support_modal_head">Resolve Ticket</h5>
                    <p className="support_modal_content">
                      Are you sure you want to resolve this ticket?
                    </p>
                  </div>

                  <div className="support_modal_btn">
                    <Button
                      className="support_cancel_btn"
                      variant="secondary"
                      onClick={handleNotShowResolve}
                    >
                      Cancel
                    </Button>

                    <Button
                      className="support_resolve_btn"
                      variant="primary"
                      onClick={handleResolve}
                    >
                      Resolve
                    </Button>
                  </div>
                </Modal.Body>
              </Modal>
            </div>

            <p className="mb-3 ticket_description">{ticketDetail?.subject}</p>
            <div className="chatbox_chat">
              <div className="chat_content" ref={chatContainerRef}>
                {/* <p className="admin_chat">Hello, How can we help you?</p> */}
                {/* <p className="user_chat">
                Yo Reddit! What’s a small thing that anyone can do at nearly
                anytime to improve their mood and make them happier?
              </p>
              <p className="user_chat">
                The More Important the Work, the More Important the Rest
              </p>
              <p className="admin_chat">
                How to design a product that can grow itself 10x in year:
                <p>
                  Understanding color theory: the color wheel and finding
                  complementary colors
                </p>
              </p>
              <p className="user_chat">
                Any mechanical keyboard enthusiasts in design?
              </p>
              <p className="admin_chat">
                How to design a product that can grow itself 10x in year:
              </p>
              <p className="user_chat">
                Yo Reddit! What’s a small thing that anyone can do at nearly
                anytime to improve their mood and make them happier?
              </p> */}
                {chatContent.length === 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px', color: '#6c757d', fontWeight: '500', fontSize: '14px' }}>
                    No messages yet
                  </div>
                )}

                {chatContent.map((message) => {
                  let isImageMsg = false;
                  let isAttachmentMsg = false;
                  let commentUrl = "";
                  let commentText = "";

                  if (message.docPath) {
                    commentUrl = message.docPath;
                    if (commentUrl && commentUrl.startsWith("/") && !commentUrl.startsWith("//")) {
                      commentUrl = DOC_URL + commentUrl;
                    }
                    commentText = message.comment || "";
                    const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(commentUrl);
                    if (isImage) {
                      isImageMsg = true;
                    } else {
                      isAttachmentMsg = true;
                    }
                  } else if (message.comment) {
                    // Fallback for older messages that used prefixed comments
                    const imageMatch = message.comment.match(/^\s*Image:\s*([^\s]+)(?:\n([\s\S]*))?$/i);
                    const fileMatch = message.comment.match(/^\s*Attachment:\s*([^\s]+)(?:\n([\s\S]*))?$/i);

                    if (imageMatch) {
                      isImageMsg = true;
                      commentUrl = imageMatch[1].trim();
                      if (commentUrl && commentUrl.startsWith("/") && !commentUrl.startsWith("//")) {
                        commentUrl = DOC_URL + commentUrl;
                      }
                      commentText = imageMatch[2]?.trim() || "";
                    } else if (fileMatch) {
                      isAttachmentMsg = true;
                      commentUrl = fileMatch[1].trim();
                      if (commentUrl && commentUrl.startsWith("/") && !commentUrl.startsWith("//")) {
                        commentUrl = DOC_URL + commentUrl;
                      }
                      commentText = fileMatch[2]?.trim() || "";
                    } else if (message.comment.toLowerCase().trim().startsWith("image:")) {
                      isImageMsg = true;
                      const parts = message.comment.split(/\n/);
                      commentUrl = parts[0].replace(/image:\s*/i, "").trim();
                      if (commentUrl && commentUrl.startsWith("/") && !commentUrl.startsWith("//")) {
                        commentUrl = DOC_URL + commentUrl;
                      }
                      commentText = parts.slice(1).join("\n").trim();
                    } else if (message.comment.toLowerCase().trim().startsWith("attachment:")) {
                      isAttachmentMsg = true;
                      const parts = message.comment.split(/\n/);
                      commentUrl = parts[0].replace(/attachment:\s*/i, "").trim();
                      if (commentUrl && commentUrl.startsWith("/") && !commentUrl.startsWith("//")) {
                        commentUrl = DOC_URL + commentUrl;
                      }
                      commentText = parts.slice(1).join("\n").trim();
                    }
                  }

                  const isAdmin = message.userType?.toLowerCase() === "admin";

                  return (
                    <div
                      className={isAdmin ? "admin_chat_wrapper" : "user_chat_wrapper"}
                      key={message.chatId || message.id || Math.random()}
                    >
                      <div className="chat_bubble_group">
                        <div className={isAdmin ? "admin_chat_bubble" : "user_chat_bubble"}>
                          {isImageMsg ? (
                            <div className="chat_image_container">
                              <img
                                src={commentUrl}
                                alt="attachment"
                                className="chat_image"
                                onClick={() => window.open(commentUrl, "_blank")}
                              />
                              {commentText && <p className="chat_text_overlay">{commentText}</p>}
                            </div>
                          ) : isAttachmentMsg ? (
                            <div className="chat_file_container">
                              <a
                                href={commentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="chat_file_link"
                              >
                                <img src="/build/img/Icons/Paperclip.svg" alt="file" width={18} height={18} style={{ filter: isAdmin ? 'none' : 'brightness(0) invert(1)' }} />
                                <span>{commentUrl.split('/').pop() || "View Attachment"}</span>
                              </a>
                              {commentText && <p className="chat_text_overlay">{commentText}</p>}
                            </div>
                          ) : (
                            <p className="chat_message_text">{message.comment}</p>
                          )}
                        </div>
                        <div className={isAdmin ? "admin_chat_info" : "user_chat_info"}>
                          {isAdmin && (
                            <span className="chat_sender_name">Support Team</span>
                          )}
                          <span className="chat_msg_time">
                            {message.createdOn?.split(" ")[1] || message.createdOn}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {selectedTicket?.status === "OPEN" ? (
              <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "8px" }}>
                {selectedFile && (
                  <div style={{ background: "#f1f3f4", padding: "8px 12px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e0e0e0", margin: "0 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden" }}>
                      <span style={{ fontSize: "18px" }}>{selectedFileType === 'image' ? '🖼️' : '📎'}</span>
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "250px", fontSize: "14px", fontWeight: "500", color: "#333" }}>{selectedFile.name}</span>
                    </div>
                    <button onClick={() => { setSelectedFile(null); setSelectedFileType(null); }} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "18px", fontWeight: "bold", padding: "0 4px", color: "#6c757d" }}>×</button>
                  </div>
                )}
                <div className="send_messages mt-2">
                  <div className="support_chat_add">
                    <img
                      className={"support_attach_img"}
                      src="/build/img/Icons/Paperclip.svg"
                      alt="zex pr wire Logo"
                      width={24}
                      height={24}
                      onClick={handleAttachmentIconClick}
                    />
                    <img
                      className={"support_add_img"}
                      src="/build/img/Icons/Add image.svg"
                      alt="zex pr wire Logo"
                      width={24}
                      height={24}
                      onClick={handleImageIconClick}
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={(e) => handleFileSelect(e, "file")}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      ref={imageInputRef}
                      style={{ display: "none" }}
                      onChange={(e) => handleFileSelect(e, "image")}
                    />
                  </div>
                  <input
                    className="support_chat_input"
                    type="text"
                    placeholder="Type Here"
                    name="comment"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSendMessage();
                    }}
                  />
                  <button
                    className="support_chat_send"
                    onClick={handleSendMessage}
                    disabled={isUploadingAttachment}
                  >
                    {isUploadingAttachment ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: "1rem", height: "1rem" }}></span>
                    ) : (
                      <img
                        className={"support_send_img"}
                        src="/build/img/Icons/Send.png"
                        alt="zex pr wire Logo"
                        width={24}
                        height={24}
                      />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
        </>
      </div>
    </>
  );
}
